import { useState } from "react";
import { toast } from "sonner";
import { Star, EyeOff, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { PageHead, Stat, EmptyState } from "@/components/common";
import { FullLoader } from "@/context/AuthContext";
import { ReviewItem } from "@/components/DoctorReviews";
import { Stars } from "@/pages/patient/SuvidhaCentre";
import { api, errMsg, fmtDate, useApi } from "@/lib/api";

export function DoctorReviewsPage() {
  const { data, loading } = useApi("/doctor/reviews");
  if (loading || !data) return <FullLoader />;
  const max = Math.max(1, ...Object.values(data.distribution));
  return (
    <div>
      <PageHead eyebrow="Feedback" title="Ratings & reviews" sub="Ratings patients gave after completed consultations. Hidden reviews don't count in your average." />
      <div className="grid sm:grid-cols-3 gap-4">
        <Stat label="Average rating" value={data.rating ? `★ ${data.rating.toFixed(1)}` : "–"} testId="doctor-rating-avg" />
        <Stat label="Ratings" value={data.rating_count} testId="doctor-rating-count" />
        <div className="surface p-4" data-testid="doctor-rating-distribution">
          {[5, 4, 3, 2, 1].map((n) => (
            <div key={n} className="flex items-center gap-2 text-xs"><span className="w-6">{n}★</span>
              <div className="h-1.5 flex-1 rounded-full bg-[#E2E8ED]"><div className="h-1.5 rounded-full bg-[#FF9700]" style={{ width: `${(data.distribution[n] / max) * 100}%` }} /></div>
              <span className="w-6 text-right muted">{data.distribution[n]}</span></div>))}
        </div>
      </div>
      <div className="surface p-5 mt-6">
        {data.items.length ? data.items.map((r) => <ReviewItem key={r.id} r={r} extra={<>{` · ${r.appointment_ref}`}{r.is_follow_up ? " · Follow-up" : ""}{r.hidden && <span className="ml-2 text-red-700">Hidden by admin</span>}</>} />)
          : <EmptyState title="No ratings yet" text="Patients can rate a consultation after you mark it complete." />}
      </div>
    </div>
  );
}

export function AdminReviews() {
  const [tab, setTab] = useState("all");
  const q = { all: "", visible: "?hidden=false", hidden: "?hidden=true", low: "?stars=1" }[tab];
  const { data, loading, reload } = useApi(`/admin/reviews${q}`);
  const [target, setTarget] = useState(null);
  const [reason, setReason] = useState("");
  const toggle = async (r, hidden, why = "") => {
    try { await api.post(`/admin/reviews/${r.id}/visibility`, { hidden, reason: why }); toast.success(hidden ? "Review hidden" : "Review restored"); setTarget(null); setReason(""); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHead eyebrow="Moderation" title="Doctor reviews" sub="Ratings are published instantly. Hide inappropriate reviews – hidden ones drop out of the doctor's average." />
      <Tabs value={tab} onValueChange={setTab}><TabsList>
        <TabsTrigger value="all" data-testid="reviews-tab-all">All</TabsTrigger><TabsTrigger value="visible" data-testid="reviews-tab-visible">Visible</TabsTrigger>
        <TabsTrigger value="hidden" data-testid="reviews-tab-hidden">Hidden</TabsTrigger><TabsTrigger value="low" data-testid="reviews-tab-low">1-star</TabsTrigger>
      </TabsList></Tabs>
      <div className="surface p-5 mt-4">
        {loading ? <FullLoader /> : data?.length ? data.map((r) => (
          <div key={r.id} className="flex flex-wrap items-start justify-between gap-3 py-3 border-b border-[#E2E8ED] last:border-0" data-testid={`admin-review-${r.id}`}>
            <div className="min-w-0 flex-1 text-sm">
              <div className="flex flex-wrap items-center gap-2"><Stars value={r.stars} size="h-3.5 w-3.5" /><span className="font-medium">{r.doctor_name}</span>
                <span className="text-xs muted">by {r.patient_first} · {r.appointment_ref} · {fmtDate(r.created_at)}</span>
                {r.hidden && <span className="text-[11px] rounded-full bg-red-50 text-red-700 px-2 py-0.5">Hidden{r.hidden_reason ? `: ${r.hidden_reason}` : ""}</span>}</div>
              {r.comment && <p className="mt-1">{r.comment}</p>}
            </div>
            {r.hidden ? <Button size="sm" variant="outline" className="rounded-full" onClick={() => toggle(r, false)} data-testid={`review-unhide-${r.id}`}><Eye className="h-3.5 w-3.5 mr-1" />Restore</Button>
              : <Button size="sm" variant="outline" className="rounded-full text-red-700" onClick={() => setTarget(r)} data-testid={`review-hide-${r.id}`}><EyeOff className="h-3.5 w-3.5 mr-1" />Hide</Button>}
          </div>)) : <EmptyState title="No reviews" text="Doctor reviews appear here as patients rate completed consultations." icon={Star} />}
      </div>
      <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Hide review</DialogTitle><DialogDescription>It will be removed from the public profile and the doctor's average.</DialogDescription></DialogHeader>
          <Textarea maxLength={300} placeholder="Reason (e.g. abusive language, personal data)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="review-hide-reason" />
          <DialogFooter><Button className="rounded-full bg-red-600 hover:bg-red-700" onClick={() => toggle(target, true, reason)} data-testid="review-hide-confirm">Hide review</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
